import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { appPath } from '../utils/appPath'
import { useProjectMixStats } from './useProjectMixStats'
import {
  computeCategoryFilteredList,
  computeCategoryOptions,
  computeOrgFYOptions,
  computeOrgPeriodKeys,
  computeOrgYearOptions,
  computeProjectList,
  computeProjectListForDisplay,
  filterAndSortProjects,
  type OrgFilterMode,
  type ProjectSortKey,
} from '../utils/projectOverviewLists'
import {
  computeCategorySummary,
  computePeriodCategorySummary,
  computePeriodSummary,
  mergeDisplaySummary,
} from '../utils/projectOverviewSummary'
import {
  computeOverviewHoursPieData,
  computeOverviewTopProjectsBarData,
  computeOverviewUtilDistribution,
} from '../utils/projectOverviewCharts'

export function useProjectAnalysisOverview() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { data: projectMixStats, loading, error, refetch } = useProjectMixStats()

  const [orgFilterMode, setOrgFilterMode] = useState<OrgFilterMode>('fy')
  const [orgFY, setOrgFY] = useState('2025-26')
  const [orgQ, setOrgQ] = useState('all')
  const [orgYear, setOrgYear] = useState('all')
  const [orgMonth, setOrgMonth] = useState('all')
  const [orgCategoryFilter, setOrgCategoryFilter] = useState('')

  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState<ProjectSortKey>('utilization_pct')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [displayCount, setDisplayCount] = useState(25)
  const [clientByProjectId, setClientByProjectId] = useState<
    Record<number, string>
  >({})

  useEffect(() => {
    let cancelled = false
    fetch(
      '/ts_project_json/client_project/client_projects_mapping_info.json',
    )
      .then((r) => (r.ok ? r.json() : []))
      .then((raw: unknown) => {
        if (cancelled) return
        const rows = Array.isArray(raw) ? raw : []
        const m: Record<number, string> = {}
        for (const row of rows) {
          const o = row as Record<string, unknown>
          const pid = Number(o.project_id)
          const nm = String(o.client_name ?? '').trim()
          if (Number.isFinite(pid) && nm) m[pid] = nm
        }
        setClientByProjectId(m)
      })
      .catch(() => {
        if (!cancelled) setClientByProjectId({})
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const q = searchParams.get('projectId')
    if (q && /^\d+$/.test(q.trim())) {
      navigate(appPath(`/project-analysis/${q.trim()}`), { replace: true })
    }
  }, [searchParams, navigate])

  const overviewState = useMemo(
    () => ({
      orgFilterMode,
      orgFY,
      orgQ,
      orgYear,
      orgMonth,
      orgCategoryFilter,
    }),
    [orgFilterMode, orgFY, orgQ, orgYear, orgMonth, orgCategoryFilter],
  )

  const orgPeriodKeys = useMemo(
    () => computeOrgPeriodKeys(overviewState),
    [overviewState],
  )

  const orgYearOptions = useMemo(
    () => computeOrgYearOptions(projectMixStats),
    [projectMixStats],
  )

  const orgFYOptions = useMemo(
    () => computeOrgFYOptions(orgYearOptions),
    [orgYearOptions],
  )

  useEffect(() => {
    if (
      orgFilterMode === 'fy' &&
      orgFYOptions.length > 0 &&
      !orgFYOptions.some((o) => o.value === orgFY)
    ) {
      setOrgFY(orgFYOptions[0]?.value ?? '2025-26')
      setOrgQ('all')
    }
  }, [orgFilterMode, orgFYOptions, orgFY])

  const projectList = useMemo(
    () => computeProjectList(projectMixStats),
    [projectMixStats],
  )

  const projectListForDisplay = useMemo(() => {
    const base = computeProjectListForDisplay(
      projectMixStats,
      orgPeriodKeys,
      projectList,
    )
    return base.map((p) => ({
      ...p,
      client_name: clientByProjectId[p.project_id] ?? null,
    }))
  }, [projectMixStats, orgPeriodKeys, projectList, clientByProjectId])

  const categoryOptions = useMemo(
    () => computeCategoryOptions(projectList),
    [projectList],
  )

  const categoryFilteredProjectList = useMemo(
    () =>
      computeCategoryFilteredList(projectListForDisplay, orgCategoryFilter),
    [projectListForDisplay, orgCategoryFilter],
  )

  const periodSummary = useMemo(
    () => computePeriodSummary(projectMixStats, orgPeriodKeys, overviewState),
    [projectMixStats, orgPeriodKeys, overviewState],
  )

  const categorySummary = useMemo(
    () =>
      computeCategorySummary(orgCategoryFilter, categoryFilteredProjectList),
    [orgCategoryFilter, categoryFilteredProjectList],
  )

  const periodCategorySummary = useMemo(
    () =>
      computePeriodCategorySummary(
        projectMixStats,
        orgPeriodKeys,
        orgCategoryFilter,
        overviewState,
      ),
    [projectMixStats, orgPeriodKeys, orgCategoryFilter, overviewState],
  )

  const displaySummary = useMemo(
    () =>
      mergeDisplaySummary(
        orgCategoryFilter,
        orgPeriodKeys,
        periodCategorySummary,
        categorySummary,
        periodSummary,
        projectMixStats?.summary,
      ),
    [
      orgCategoryFilter,
      orgPeriodKeys,
      periodCategorySummary,
      categorySummary,
      periodSummary,
      projectMixStats?.summary,
    ],
  )

  const overviewHoursPieData = useMemo(
    () => computeOverviewHoursPieData(displaySummary),
    [displaySummary],
  )

  const overviewTopProjectsBarData = useMemo(
    () => computeOverviewTopProjectsBarData(categoryFilteredProjectList),
    [categoryFilteredProjectList],
  )

  const overviewUtilDistribution = useMemo(
    () => computeOverviewUtilDistribution(categoryFilteredProjectList),
    [categoryFilteredProjectList],
  )

  const filteredAndSortedProjects = useMemo(
    () =>
      filterAndSortProjects(
        categoryFilteredProjectList,
        search,
        sortBy,
        sortOrder,
      ),
    [categoryFilteredProjectList, search, sortBy, sortOrder],
  )

  const displayedProjects = filteredAndSortedProjects.slice(0, displayCount)
  const remainingCount = Math.max(
    0,
    filteredAndSortedProjects.length - displayCount,
  )

  const exportLabelParts = [
    orgCategoryFilter,
    orgPeriodKeys?.length
      ? orgFilterMode === 'fy'
        ? `FY${orgFY}`
        : orgYear
      : 'all',
  ]

  const toggleSort = (key: ProjectSortKey) => {
    if (sortBy === key) setSortOrder((o) => (o === 'desc' ? 'asc' : 'desc'))
    else {
      setSortBy(key)
      setSortOrder('desc')
    }
  }

  return {
    loading,
    error,
    refetch,
    navigate,
    orgFilterMode,
    setOrgFilterMode,
    orgFY,
    setOrgFY,
    orgQ,
    setOrgQ,
    orgYear,
    setOrgYear,
    orgMonth,
    setOrgMonth,
    orgCategoryFilter,
    setOrgCategoryFilter,
    orgPeriodKeys,
    orgYearOptions,
    orgFYOptions,
    categoryOptions,
    displaySummary,
    overviewHoursPieData,
    overviewTopProjectsBarData,
    overviewUtilDistribution,
    search,
    setSearch,
    sortBy,
    sortOrder,
    toggleSort,
    displayCount,
    setDisplayCount,
    displayedProjects,
    filteredAndSortedProjects,
    remainingCount,
    exportLabelParts,
  }
}
